// eventPricing.js — Road Mode: per-event pricing (shift.pricing).
//
// The nullable `pricing` block on the shift doc overrides the static price
// tables for ONE shift — stamped at open from an Event Config profile and
// dying with the shift, so closing the shift IS the revert. Server authority:
// shiftPricingServer() in functions/index.js (checkouts charge from it via
// ad-hoc Square line items — the catalog is never edited). Everything here is
// DISPLAY-ONLY, same doctrine as src/utils/combos.js.
//
// Absent block ⇒ every helper falls back to the static constants, so home
// shifts render exactly as before.

import { COMBOS, CUSTOM_BASE_CENTS } from "./combos";

// Extras Dan has retired — filtered out even when a saved profile / staged
// setup still carries them, so old data can't resurrect a dropped upsell.
// "toppings" retired 2026-08-28: sauces are a free self-serve bar, not a
// $3 gate ("seems a little nickle and dimey" — Dan).
const RETIRED_EXTRA_KEYS = new Set(["toppings"]);

const FLASH_BASE_CENTS = 1000;
// Kit $15 + frame $5 — the goods half of the à-la-carte comparison figures.
const KIT_FRAME_CENTS = 2000;
const PRINT_BASE_CENTS = 1000;

function cents(v) {
  return Number.isFinite(v) && v > 0 && v < 100000 ? Math.round(v) : null;
}

// Normalized pricing block, or null when the shift has none (= home mode).
export function shiftPricing(shift) {
  const p = shift?.pricing;
  if (!p || typeof p !== "object") return null;
  return {
    flashCents: cents(p.flashCents),
    customCents: cents(p.customCents),
    combos: {
      artist: cents(p.combos?.artist),
      collector: cents(p.combos?.collector),
    },
    extras: Array.isArray(p.extras)
      ? p.extras
          .filter((e) => e && typeof e.key === "string" && !RETIRED_EXTRA_KEYS.has(e.key) && cents(e.cents))
          .map((e) => ({
            key: e.key,
            label: typeof e.label === "string" && e.label.trim() ? e.label.trim() : e.key,
            note: typeof e.note === "string" ? e.note : "",
            emoji: typeof e.emoji === "string" ? e.emoji : "✨",
            cents: cents(e.cents),
            addOnType: typeof e.addOnType === "string" ? e.addOnType : null,
          }))
      : [],
    menuNote: typeof p.menuNote === "string" && p.menuNote.trim() ? p.menuNote.trim() : null,
    taxPct: Number.isFinite(p.taxPct) && p.taxPct > 0 && p.taxPct < 30 ? p.taxPct : null,
    taxLabel: typeof p.taxLabel === "string" && p.taxLabel.trim() ? p.taxLabel.trim() : "Sales Tax",
    eventTag: typeof p.eventTag === "string" && p.eventTag.trim() ? p.eventTag.trim() : null,
    squareLocationName:
      typeof p.squareLocationName === "string" && p.squareLocationName.trim()
        ? p.squareLocationName.trim()
        : null,
    hideStandby: p.hideStandby === true,
  };
}

export function flashPriceCents(shift) {
  return shiftPricing(shift)?.flashCents ?? FLASH_BASE_CENTS;
}

export function customPriceCents(shift) {
  return shiftPricing(shift)?.customCents ?? CUSTOM_BASE_CENTS;
}

export function tierPriceCents(shift, orderType) {
  return orderType === "custom" ? customPriceCents(shift) : flashPriceCents(shift);
}

export function comboPriceCents(shift, comboKey) {
  return shiftPricing(shift)?.combos?.[comboKey] ?? COMBOS[comboKey]?.priceCents ?? null;
}

// À-la-carte comparison figure for strikethroughs: custom base + kit/frame
// (+ the print for collector, at the event's print-extra price when one is
// defined). Only renders as a discount when it exceeds the combo price —
// PromoPanel and the interstitial already guard on that.
export function comboAlaCarteCents(shift, comboKey) {
  const p = shiftPricing(shift);
  if (!p) return COMBOS[comboKey]?.alaCarteCents ?? null;
  const printCents = p.extras.find((e) => e.addOnType === "signed_print")?.cents ?? PRINT_BASE_CENTS;
  const base = (p.customCents ?? CUSTOM_BASE_CENTS) + KIT_FRAME_CENTS;
  return comboKey === "collector" ? base + printCents : base;
}

// Delta for an already-paid custom → combo. Mirrors the server's road-mode
// arithmetic (combo − custom); static fallback keeps the old constants.
export function comboUpgradeDeltaCentsForShift(shift, comboKey) {
  const p = shiftPricing(shift);
  if (!p) return (COMBOS[comboKey]?.priceCents ?? 0) - CUSTOM_BASE_CENTS;
  const delta =
    (p.combos?.[comboKey] ?? COMBOS[comboKey]?.priceCents ?? 0) -
    (p.customCents ?? CUSTOM_BASE_CENTS);
  return delta > 0 ? delta : (COMBOS[comboKey]?.priceCents ?? 0) - CUSTOM_BASE_CENTS;
}

// Purchasable extras for this shift ([] when no pricing block — extras are a
// Road Mode feature). Order preserved from the block.
export function activeExtras(shift) {
  return shiftPricing(shift)?.extras ?? [];
}

// Road Mode can hide the free-standby tier on /join (solo events: every queue
// slot paid; comps go through the register QR instead).
export function hideStandbyTier(shift) {
  return shiftPricing(shift)?.hideStandby === true;
}

// Seed extras for the Event Config editor — Dan toggles each on/off and edits
// the price; addOnType wires the fulfillment badge/delivery-checkpoint rails.
export const EXTRA_PRESETS = [
  {
    key: "signed_print",
    label: "Signed Artist's Print",
    emoji: "✍️",
    cents: 1500,
    addOnType: "signed_print",
    note: "Instant photo print of your pancake, signed + numbered, in an envelope",
  },
];
